import { NavLink, Navigate, Outlet } from 'react-router-dom';

import { logoutAdminSession } from '@/api/adminSession';
import Button from '@/components/ui/Button/Button';
import { routePaths } from '@/routes/routeRegistry';
import { useAdminAuthStore } from '@/stores/useAdminAuthStore';
import { useToastStore } from '@/stores/useToastStore';

import styles from './AdminConsoleLayout.module.scss';

const adminNavigationItems = [
  {
    label: '공지사항',
    to: routePaths.adminNotices,
  },
  {
    label: '팝업',
    to: routePaths.adminPopups,
  },
  {
    label: 'Q&A',
    to: routePaths.adminQna,
  },
  {
    label: '자료실',
    to: routePaths.adminResources,
  },
  {
    label: '회원관리',
    to: routePaths.adminEnrollments,
  },
  {
    label: '일정관리',
    to: routePaths.adminPracticum,
  },
  {
    label: '예약문의',
    to: routePaths.adminProgramReservations,
  },
  {
    label: '프로그램 관리',
    to: routePaths.adminPrograms,
  },
  {
    label: '프로그램 수강생 관리',
    to: routePaths.adminProgramEnrollments,
  },
  {
    label: '프로그램 카테고리 관리',
    to: routePaths.adminProgramMenus,
  },
  {
    label: '문제영역 관리',
    to: routePaths.adminProblemAreas,
  },
  {
    label: '결제 관리',
    to: routePaths.adminPayments,
  },
] as const;

const AdminConsoleLayout = () => {
  const adminDisplayName = useAdminAuthStore((state) => state.adminDisplayName);
  const isAuthenticated = useAdminAuthStore((state) => state.isAuthenticated);
  const showToast = useToastStore((state) => state.showToast);
  if (!isAuthenticated) {
    return <Navigate replace to={routePaths.adminLogin} />;
  }

  const handleLogout = () => {
    void logoutAdminSession().catch(() => {
      // Local logout is immediate even if the server is temporarily unreachable.
    });
    showToast({
      message: '관리자 세션을 종료했습니다.',
      variant: 'success',
    });
  };

  return (
    <div className={styles['layout']}>
      <aside className={styles['sidebar']}>
        <div className={styles['sidebarHeader']}>
          <h1 className={styles['title']}>관리자</h1>
          <p className={styles['adminName']}>{adminDisplayName || '관리자'}</p>
        </div>

        <nav aria-label='관리자 메뉴' className={styles['navigation']}>
          <ul className={styles['navigationList']}>
            {adminNavigationItems.map((item) => {
              return (
                <li key={item.to}>
                  <NavLink
                    className={({ isActive }) =>
                      isActive
                        ? `${styles['navigationLink']} ${styles['navigationLinkActive']}`
                        : styles['navigationLink']
                    }
                    end={item.to === routePaths.admin}
                    to={item.to}
                  >
                    <span className={styles['navigationLabel']}>{item.label}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className={styles['sidebarFooter']}>
          <Button onClick={handleLogout} variant='secondary'>
            로그아웃
          </Button>
        </div>
      </aside>

      <main className={styles['main']}>
        <Outlet />
      </main>
    </div>
  );
};

export default AdminConsoleLayout;
